import { Card, CardContent, Input } from '@tark/shared/components';
import type { TenantScheduleDraftSlot } from '@tark/shared/types';
import { MoveRight } from 'lucide-react';
import { fromLocalDateTimeInput, toLocalDateTimeInput } from '../../lib/scheduleDrafts';

type Props = {
  slot: TenantScheduleDraftSlot;
  editable: boolean;
  selectedAthleteId: number | null;
  onChange: (slot: TenantScheduleDraftSlot) => void;
  onSelectAthlete: (athleteId: number) => void;
};

export default function ScheduleDraftSessionCard({ slot, editable, selectedAthleteId, onChange, onSelectAthlete }: Props) {
  const full = slot.capacity != null && slot.assignments.length >= slot.capacity;
  return (
    <Card variant="default" className={!slot.included ? 'opacity-60' : undefined}>
      <CardContent className="space-y-4 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-text">Proposed session</p>
            <p className="mt-1 text-sm text-text-muted">{slot.assignments.length}{slot.capacity != null ? ` of ${slot.capacity}` : ''} students {full ? '- full' : ''}</p>
          </div>
          <label className="flex min-h-10 items-center gap-2 text-sm font-medium text-text">
            <input type="checkbox" checked={slot.included} disabled={!editable || slot.assignments.length > 0} onChange={(event) => onChange({ ...slot, included: event.target.checked })} className="h-4 w-4 accent-primary" />
            Include
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Starts" type="datetime-local" required disabled={!editable} value={toLocalDateTimeInput(slot.startsAt)} onChange={(event) => { if (event.target.value) onChange({ ...slot, startsAt: fromLocalDateTimeInput(event.target.value) }); }} />
          <Input label="Ends" type="datetime-local" required disabled={!editable} value={toLocalDateTimeInput(slot.endsAt)} onChange={(event) => { if (event.target.value) onChange({ ...slot, endsAt: fromLocalDateTimeInput(event.target.value) }); }} />
          <Input label="Location" disabled={!editable} value={slot.locationLabel || ''} placeholder="Optional" onChange={(event) => onChange({ ...slot, locationLabel: event.target.value || null })} />
          <Input label="Capacity" type="number" min={1} disabled={!editable} value={slot.capacity ?? ''} placeholder="No limit" onChange={(event) => onChange({ ...slot, capacity: event.target.value ? Number(event.target.value) : null })} />
        </div>

        <div className="divide-y divide-border rounded-lg border border-border">
          {slot.assignments.length === 0 ? <p className="px-3 py-4 text-sm text-text-muted">No students assigned.</p> : slot.assignments.map((assignment) => (
            <button key={assignment.athleteId} type="button" disabled={!editable} onClick={() => onSelectAthlete(assignment.athleteId)} className={`flex min-h-12 w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-surface-elevated disabled:cursor-default ${selectedAthleteId === assignment.athleteId ? 'bg-primary/10' : ''}`}>
              <span><span className="block text-sm font-medium text-text">{assignment.athleteName}</span>{assignment.preferenceRank ? <span className="text-xs text-text-muted">Family choice {assignment.preferenceRank}</span> : null}</span>
              {editable ? <MoveRight className="h-4 w-4 text-primary" /> : null}
            </button>
          ))}
        </div>
        {slot.assignments.length > 0 && editable ? <p className="text-xs text-text-muted">Move students before excluding this session.</p> : null}
      </CardContent>
    </Card>
  );
}
